import React, { useRef, useEffect } from 'react';
import { 
  FaBold, 
  FaItalic, 
  FaUnderline, 
  FaAlignLeft, 
  FaAlignCenter, 
  FaAlignRight, 
  FaListOl, 
  FaListUl,
  FaHeading
} from 'react-icons/fa';

const RichTextEditor = ({ value, onChange, placeholder = 'Enter description...' }) => {
  const editorRef = useRef(null);

  // Sync state to editor innerHTML (only when they actually differ to keep cursor position)
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      // If empty placeholder content is left by browser, treat as empty string
      if (html === '<p><br></p>' || html === '<br>' || html.trim() === '') {
        onChange('');
      } else {
        onChange(html);
      }
    }
  };

  const execCommand = (command, val = null) => {
    document.execCommand(command, false, val);
    handleInput();
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    const htmlData = clipboardData.getData('text/html');
    const textData = clipboardData.getData('text/plain');

    if (htmlData) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlData;

      // 1. First Pass: Handle Google Docs outer wrapper <b style="font-weight:normal">
      const allElements = Array.from(tempDiv.getElementsByTagName('*'));
      allElements.forEach((el) => {
        if (!el.parentNode) return;
        const tagName = el.tagName.toUpperCase();
        const style = el.getAttribute('style') || '';
        
        if ((tagName === 'B' || tagName === 'STRONG') && 
            (style.includes('font-weight: normal') || style.includes('font-weight:normal') || 
             style.includes('font-weight: 400') || style.includes('font-weight:400'))) {
          const span = document.createElement('span');
          span.innerHTML = el.innerHTML;
          el.parentNode.replaceChild(span, el);
        }
      });

      // 2. Second Pass: Convert style-based bold/italic/underline to structural tags and clean styles
      const remainingElements = Array.from(tempDiv.getElementsByTagName('*'));
      remainingElements.forEach((el) => {
        if (!el.parentNode) return;
        const style = el.getAttribute('style') || '';
        const tagName = el.tagName.toUpperCase();
        const isContainer = ['DIV', 'UL', 'OL', 'BODY', 'HTML', 'TABLE', 'TR', 'THEAD', 'TBODY'].includes(tagName);

        const isBold = style.includes('font-weight: bold') || style.includes('font-weight:bold') || 
                       style.includes('font-weight: 600') || style.includes('font-weight:600') || 
                       style.includes('font-weight: 700') || style.includes('font-weight:700') || 
                       style.includes('font-weight: 800') || style.includes('font-weight:800') || 
                       style.includes('font-weight: 900') || style.includes('font-weight:900') || 
                       style.includes('font-weight: bolder') || style.includes('font-weight:bolder');
        const isItalic = style.includes('font-style: italic') || style.includes('font-style:italic');
        const isUnderline = style.includes('text-decoration: underline') || style.includes('text-decoration:underline');
        
        // Remove style, class, and id attributes
        el.removeAttribute('style');
        el.removeAttribute('class');
        el.removeAttribute('id');

        // Apply HTML tags for formatting if style matched and it is not a container
        if (!isContainer) {
          if (isBold) {
            el.innerHTML = `<strong>${el.innerHTML}</strong>`;
          }
          if (isItalic) {
            el.innerHTML = `<em>${el.innerHTML}</em>`;
          }
          if (isUnderline) {
            el.innerHTML = `<u>${el.innerHTML}</u>`;
          }
        }
      });

      const cleanedHtml = tempDiv.innerHTML;
      document.execCommand('insertHTML', false, cleanedHtml);
    } else if (textData) {
      const formattedText = textData.replace(/\r?\n/g, '<br>');
      document.execCommand('insertHTML', false, formattedText);
    }
    handleInput();
  };

  return (
    <div className="custom-rich-editor">
      {/* Toolbar */}
      <div className="editor-toolbar">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="editor-btn"
          title="Bold"
        >
          <FaBold size={11} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="editor-btn"
          title="Italic"
        >
          <FaItalic size={11} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="editor-btn"
          title="Underline"
        >
          <FaUnderline size={11} />
        </button>

        <span className="editor-divider" />

        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h3>')}
          className="editor-btn"
          title="Heading"
        >
          <FaHeading size={11} />
        </button>

        <span className="editor-divider" />

        <button
          type="button"
          onClick={() => execCommand('justifyLeft')}
          className="editor-btn"
          title="Align Left"
        >
          <FaAlignLeft size={11} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyCenter')}
          className="editor-btn"
          title="Align Center"
        >
          <FaAlignCenter size={11} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyRight')}
          className="editor-btn"
          title="Align Right"
        >
          <FaAlignRight size={11} />
        </button>

        <span className="editor-divider" />

        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="editor-btn"
          title="Bullet List"
        >
          <FaListUl size={11} />
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="editor-btn"
          title="Numbered List"
        >
          <FaListOl size={11} />
        </button>
      </div>

      {/* Editor Content Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="editor-body"
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;
